import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { CheckCircle, XCircle, FileText, Buildings, Shield, Warning, Rocket, Wallet, CaretDown, Bank } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { useAuth } from "@/contexts/AuthContext";
import { SAP_BANK_DIRECTORY } from "@/data/sapBankDirectory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

// Feb 2026, user's explicit ask: these are the ONLY Account Determination
// Group codes seen across a live scan of this tenant's ~2,200 real active
// suppliers - Finance picks per-supplier, never auto-guessed.
const ACCOUNT_DETERMINATION_GROUPS = ["Z001", "Z002", "Z003", "Z004", "Z005", "Z006", "Z007"];
// Feb 2026, user's explicit ask ("ADG name also for good understanding") -
// these are SAP's own Business Configuration > Fine-Tune names (Details:
// Groups For Payables), supplied directly by the user from their SAP screen.
const ACCOUNT_DETERMINATION_GROUP_NAMES = {
  Z001: "Creditors For Raw Material (Domestic)",
  Z002: "Creditors For Raw Material (Import)",
  Z003: "Creditors For Services (Import)",
  Z004: "Creditors For Services (Domestics)",
  Z005: "Creditors For Fixed Assets",
  Z006: "Creditors For Transport",
  Z007: "Creditors For Inter company",
};
// Feb 2026, user's explicit ask ("Add a payment term selection") - confirmed
// live via SAP's own real data (CashDiscountTermsCode on PurchasingData,
// a SEPARATE node from PaymentData) - sorted by real-world frequency across
// ~2,965 suppliers, "0001" is by far the most common (~75%).
const PAYMENT_TERMS = ["0001", "0010", "Z008", "Z002", "0012", "Z003", "0008", "0002", "Z010", "0004", "Z007", "Z001", "Z006", "Z005", "0003"];
// Feb 2026, user's explicit ask ("show description of payment term also") -
// SAP's own Business Configuration > Fine-Tune > Payment Terms screen
// ("Description" column), supplied directly by the user from their SAP screen.
const PAYMENT_TERM_NAMES = {
  "0001": "Due net payable immediately",
  "0002": "7 days due net",
  "0003": "14 days due net",
  "0004": "30 days due net",
  "0005": "14 days 2%, 30 days due net",
  "0006": "14 days 3%, 30 days due net",
  "0007": "14 days 3%, 30 days 2%, 60 days due net",
  "0008": "45 days from Invoice Date",
  "0009": "End of Month of Invoice Date, 45 days",
  "0010": "30 days from Invoice Date",
  "0011": "End of Month of Invoice Date, 30 days",
  "0012": "60 days from Invoice Date",
  "0013": "End of Month of Invoice Date, 60 days",
  "0014": "75 days from Invoice Date",
  "0015": "End of Month of Invoice Date, 75 days",
  Z001: "10 days from invoice date",
  Z002: "15 days from invoice date",
  Z003: "2 days from invoice date",
  Z004: "3 days from invoice date",
  Z005: "4 days from invoice date",
  Z006: "5 days from invoice date",
  Z007: "90 days from invoice date",
  Z008: "1 days from invoice date",
  Z009: "8 days from invoice date",
  Z010: "21 days from invoice date",
};
const PAYMENT_COMPANIES = [
  { id: "RI", label: "RI" },
  { id: "RT", label: "RT" },
];

const STATUS_BADGE = {
  pending: "bg-[#E3A008]/15 text-[#8A6116] rounded-sm",
  approved: "bg-[#10B981]/15 text-[#0B7A56] rounded-sm",
  rejected: "bg-[#E02424]/10 text-[#B91C1C] rounded-sm",
};

export default function SupplierOnboardingApprovalPage() {
  const { hasPageAccess, user } = useAuth();
  const canApprove = hasPageAccess("supplier_onboarding_approval") || user?.role === "admin";
  // Feb 2026, user's explicit ask: two-stage create - procurement's
  // Approve only creates the supplier in SAP as "In Preparation"; a
  // separate "Finance Activate" permission flips it to Active, matching
  // the user's own business process ("finance approval activates them").
  const canActivate = hasPageAccess("supplier_finance_activation") || user?.role === "admin" || user?.role === "super_admin";
  const [status, setStatus] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async (s) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/supplier-onboarding-requests`, { params: { status: s } });
      setRequests(data || []);
    } catch (err) {
      toast.error("Could not load supplier requests", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (id) => {
    setApproving(true);
    try {
      const { data } = await axios.post(`${API}/supplier-onboarding-requests/${id}/approve`);
      toast.success("Supplier created in SAP (In Preparation)", {
        description: `SAP Supplier ID: ${data.sap_supplier_internal_id} - no financial data yet, use "Update Financial Data" once approved`,
      });
      setDetail(null);
      load(status);
    } catch (err) {
      toast.error("SAP creation failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    try {
      await axios.post(`${API}/supplier-onboarding-requests/${rejectTarget}/reject`, { reason: rejectReason });
      toast.success("Request rejected");
      setRejectTarget(null);
      setRejectReason("");
      setDetail(null);
      load(status);
    } catch (err) {
      toast.error("Rejection failed", { description: err?.response?.data?.detail || err.message });
    }
  };

  // Feb 2026 REDESIGN, user's explicit ask: "give a button for update
  // financial data in the sap" - replaces the old plain one-click
  // Activate. Opens a form (companies/bank/tax) pre-filled from the
  // registration data; submitting pushes whatever's filled in to SAP
  // AND activates the supplier in the same call.
  const [financialTarget, setFinancialTarget] = useState(null);
  const [financialForm, setFinancialForm] = useState(null);
  const [submittingFinancial, setSubmittingFinancial] = useState(false);
  const [bankPickerOpen, setBankPickerOpen] = useState(false);

  const openFinancialModal = (r) => {
    setFinancialTarget(r);
    setFinancialForm({
      companies: { RI: true, RT: true },
      account_determination_group: "Z001",
      cash_discount_terms_code: r.cash_discount_terms_code || "0001",
      bank_routing_id: r.bank_routing_id || "",
      bank_account_number: r.bank_account_number || "",
      bank_account_holder_name: r.bank_account_holder_name || "",
      bank_ifsc: r.bank_ifsc || "",
      gstin: r.gstin || "",
      pan: r.pan || "",
    });
  };

  const submitFinancialData = async () => {
    if (!financialTarget || !financialForm) return;
    setSubmittingFinancial(true);
    try {
      const companies = PAYMENT_COMPANIES.filter((c) => financialForm.companies[c.id]).map((c) => ({
        company_id: c.id,
        account_determination_group: financialForm.account_determination_group,
      }));
      const { data } = await axios.post(`${API}/supplier-onboarding-requests/${financialTarget.id}/submit-financial-data`, {
        companies,
        cash_discount_terms_code: financialForm.cash_discount_terms_code || null,
        bank_routing_id: financialForm.bank_routing_id || null,
        bank_account_number: financialForm.bank_account_number || null,
        bank_account_holder_name: financialForm.bank_account_holder_name || null,
        bank_ifsc: financialForm.bank_ifsc || null,
        gstin: financialForm.gstin || null,
        pan: financialForm.pan || null,
      });
      if (data.financial_data_warning) {
        toast.warning("Financial data pushed with some issues", { description: data.financial_data_warning });
      } else {
        toast.success("Financial data pushed to SAP", { description: `${data.sap_supplier_internal_id} is now Active in SAP` });
      }
      setFinancialTarget(null);
      setFinancialForm(null);
      setDetail(null);
      load(status);
    } catch (err) {
      toast.error("Could not push financial data", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSubmittingFinancial(false);
    }
  };

  // Feb 2026, user's explicit ask: Finance's single "Credit check & tax
  // compliance verified" checkbox is a REQUIRED gate before Update
  // Financial Data - enforced server-side too
  // (supplier_onboarding_service.submit_financial_data), this is never
  // just a frontend-only disabled button.
  const [verifyingCompliance, setVerifyingCompliance] = useState(false);
  const verifyCompliance = async (id) => {
    setVerifyingCompliance(true);
    try {
      const { data } = await axios.post(`${API}/supplier-onboarding-requests/${id}/verify-compliance`);
      setDetail(data);
      load(status);
    } catch (err) {
      toast.error("Could not verify compliance", { description: err?.response?.data?.detail || err.message });
    } finally {
      setVerifyingCompliance(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F6F7] font-sans" data-testid="supplier-onboarding-approvals-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">New Supplier Approval</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>
      <div className="max-w-[1400px] mx-auto p-4 md:p-6">
        <div className="flex items-center gap-2 mb-1">
          <Buildings size={18} weight="fill" className="text-[#0076CC]" />
          <h1 className="font-sans text-base font-bold text-[#111827]">New Supplier Approval</h1>
        </div>
        <p className="text-sm text-[#5B738B]">
          {canApprove
            ? "Review new supplier requests submitted by purchasing. Approving creates the supplier directly in SAP."
            : "View-only access to new supplier requests."}
        </p>

        <div className="flex items-center gap-4 mt-5 border-b border-[#CBD3DB]">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              className={`px-1 pb-2 text-sm font-semibold font-sans border-b-2 transition-colors duration-150 ${
                status === t.key ? "border-[#0076CC] text-[#0076CC]" : "border-transparent text-[#5B738B] hover:text-[#111827]"
              }`}
              data-testid={`supplier-onboarding-approvals-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-4 bg-white border border-[#CBD3DB] rounded-sm shadow-sm overflow-hidden">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-[#F5F6F7] text-[#5B738B] text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Company</th>
                <th className="text-left px-3 py-2 font-semibold">GSTIN</th>
                <th className="text-left px-3 py-2 font-semibold">Contact</th>
                <th className="text-left px-3 py-2 font-semibold">Requested By</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-right px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5B738B]" data-testid="supplier-onboarding-approvals-loading">Loading...</td></tr>
              )}
              {!loading && requests.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5B738B]" data-testid="supplier-onboarding-approvals-empty">No {status} requests.</td></tr>
              )}
              {!loading && requests.map((r) => (
                <tr key={r.id} className="border-b border-[#CBD3DB] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setDetail(r)} data-testid={`supplier-onboarding-approvals-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.company_name}</td>
                  <td className="px-3 py-2 font-data text-[#5B738B]">{r.gstin || "—"}</td>
                  <td className="px-3 py-2 text-[#5B738B]">{r.contact_name}<br />{r.contact_email}</td>
                  <td className="px-3 py-2 text-[#5B738B]">{r.requested_by_name || r.requested_by_email}</td>
                  <td className="px-3 py-2">
                    <Badge className={STATUS_BADGE[r.status]}>{r.status}</Badge>
                    {r.status === "approved" && (
                      <div className="text-[10px] mt-0.5" data-testid={`supplier-onboarding-sap-lifecycle-${r.id}`}>
                        {r.activated_at ? (
                          <span className="text-[#0B7A56]">Active in SAP</span>
                        ) : (
                          <span className="text-[#8A6116]">In Preparation - pending activation</span>
                        )}
                      </div>
                    )}
                    {r.sap_write_error && <div className="text-[#B91C1C] text-[10px] mt-0.5 flex items-center gap-0.5"><Warning size={10} /> SAP error</div>}
                    {r.financial_data_warning && <div className="text-[#B45309] text-[10px] mt-0.5 flex items-center gap-0.5" data-testid={`supplier-onboarding-financial-warning-${r.id}`}><Warning size={10} /> Financial data issue</div>}
                  </td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {r.status === "pending" && canApprove && (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" disabled={approving} onClick={() => approve(r.id)} className="rounded-sm bg-[#10B981] hover:bg-[#0B7A56] transition-colors duration-150" data-testid={`supplier-onboarding-approve-${r.id}`}>
                          <CheckCircle size={14} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRejectTarget(r.id)} className="rounded-sm border-[#E02424]/40 text-[#B91C1C]" data-testid={`supplier-onboarding-reject-${r.id}`}>
                          <XCircle size={14} className="mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                    {r.status === "approved" && !r.activated_at && canActivate && (
                      <Button
                        size="sm" disabled={!r.credit_tax_compliance_verified} onClick={() => openFinancialModal(r)}
                        title={!r.credit_tax_compliance_verified ? "Open this row to verify credit check & tax compliance first" : undefined}
                        className="rounded-sm bg-[#0076CC] hover:bg-[#005FA3] transition-colors duration-150" data-testid={`supplier-onboarding-financial-data-${r.id}`}
                      >
                        <Wallet size={14} className="mr-1" /> Update Financial Data
                      </Button>
                    )}
                    {r.status === "approved" && r.activated_at && canActivate && (
                      <Button
                        size="sm" variant="outline" disabled={!r.credit_tax_compliance_verified} onClick={() => openFinancialModal(r)}
                        title={!r.credit_tax_compliance_verified ? "Open this row to verify credit check & tax compliance first" : undefined}
                        className="rounded-sm border-[#0076CC]/40 text-[#0076CC]" data-testid={`supplier-onboarding-financial-data-update-${r.id}`}
                      >
                        <Wallet size={14} className="mr-1" /> Update Financial Data
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="supplier-onboarding-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans">{detail?.company_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div>
                <Badge className={STATUS_BADGE[detail.status]}>{detail.status}</Badge>
                {detail.sap_supplier_internal_id && (
                  <span className="ml-2 text-xs text-[#0B7A56]">SAP Supplier ID: {detail.sap_supplier_internal_id}</span>
                )}
                {detail.status === "approved" && (
                  <span className={`ml-2 text-xs ${detail.activated_at ? "text-[#0B7A56]" : "text-[#8A6116]"}`} data-testid="supplier-onboarding-detail-sap-lifecycle">
                    {detail.activated_at ? `Active in SAP (by ${detail.activated_by})` : "In Preparation - pending Finance activation"}
                  </span>
                )}
                {detail.sap_write_error && (
                  <div className="mt-2 text-xs text-[#B91C1C] bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-2">{detail.sap_write_error}</div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div><span className="text-[#5B738B]">Company:</span> {detail.company_name}</div>
                <div><span className="text-[#5B738B]">Category:</span> {detail.category || "—"}</div>
                <div><span className="text-[#5B738B]">GSTIN:</span> <span className="font-data">{detail.gstin || "—"}</span></div>
                <div><span className="text-[#5B738B]">PAN:</span> <span className="font-data">{detail.pan || "—"}</span></div>
                <div><span className="text-[#5B738B]">Contact:</span> {detail.contact_name}</div>
                <div><span className="text-[#5B738B]">Email:</span> {detail.contact_email}</div>
                <div><span className="text-[#5B738B]">Phone:</span> {detail.contact_phone}</div>
                <div><span className="text-[#5B738B]">Secondary Contact:</span> {detail.secondary_contact_name || "—"}</div>
                <div><span className="text-[#5B738B]">Secondary Phone:</span> {detail.secondary_phone || "—"}</div>
                <div className="col-span-2"><span className="text-[#5B738B]">Address:</span> {detail.address_line}, {detail.city}, {detail.state} - {detail.pincode}</div>
                <div><span className="text-[#5B738B]">Bank Account:</span> {detail.bank_account_number || "—"}</div>
                <div><span className="text-[#5B738B]">IFSC:</span> <span className="font-data">{detail.bank_ifsc || "—"}</span></div>
                <div><span className="text-[#5B738B]">Account Holder:</span> {detail.bank_account_holder_name || "—"}</div>
                <div><span className="text-[#5B738B]">Currency:</span> {detail.currency || "—"}</div>
                <div><span className="text-[#5B738B]">Requested By:</span> {detail.requested_by_name || detail.requested_by_email}</div>
                <div><span className="text-[#5B738B]">Submitted:</span> {detail.submitted_at ? new Date(detail.submitted_at).toLocaleString("en-IN") : "—"}</div>
                {detail.rejection_reason && <div className="col-span-2"><span className="text-[#5B738B]">Rejection Reason:</span> {detail.rejection_reason}</div>}
                {detail.financial_data_submitted_at && (
                  <div className="col-span-2">
                    <span className="text-[#5B738B]">Financial Data pushed:</span>{" "}
                    {new Date(detail.financial_data_submitted_at).toLocaleString("en-IN")} by {detail.financial_data_submitted_by}
                    {detail.financial_data_warning && (
                      <div className="flex items-start gap-1.5 mt-1 text-[#B45309]" data-testid="supplier-onboarding-detail-financial-warning">
                        <Warning size={14} className="mt-0.5 shrink-0" /> {detail.financial_data_warning}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-bold uppercase text-[#5B738B] mb-1.5">Attachments ({detail.attachments?.length || 0})</div>
                {detail.attachments?.length ? (
                  <div className="flex flex-col gap-1.5">
                    {detail.attachments.map((a, i) => (
                      <a
                        key={i}
                        href={`${API}/supplier-onboarding-requests/${detail.id}/attachments/${encodeURIComponent(a.filename)}`}
                        target="_blank" rel="noreferrer"
                        className="text-[#0076CC] text-xs flex items-center gap-1.5 underline"
                        data-testid={`supplier-onboarding-attachment-link-${i}`}
                      >
                        <FileText size={13} /> {a.filename} ({a.size < 1024 ? `${a.size} B` : `${(a.size / 1024).toFixed(1)} KB`})
                      </a>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-[#98A2B3]">No attachments</span>
                )}
              </div>

              {detail.status === "pending" && canApprove && (
                <div className="flex justify-end gap-2 pt-2 border-t border-[#EAECF0]">
                  <Button variant="outline" className="rounded-sm border-[#E02424]/40 text-[#B91C1C]" onClick={() => setRejectTarget(detail.id)} data-testid="supplier-onboarding-detail-reject-button">
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                  <Button disabled={approving} className="rounded-sm bg-[#10B981] hover:bg-[#0B7A56]" onClick={() => approve(detail.id)} data-testid="supplier-onboarding-detail-approve-button">
                    <CheckCircle size={14} className="mr-1" /> {approving ? "Creating in SAP..." : "Approve & Create in SAP"}
                  </Button>
                </div>
              )}
              {detail.status === "approved" && canActivate && (
                <div className="flex flex-col items-end gap-2 pt-2 border-t border-[#EAECF0]">
                  <label
                    className="flex items-center gap-2 text-xs text-[#1D2939] cursor-pointer select-none"
                    data-testid="supplier-onboarding-compliance-checkbox-label"
                  >
                    <Checkbox
                      checked={!!detail.credit_tax_compliance_verified}
                      disabled={!!detail.credit_tax_compliance_verified || verifyingCompliance}
                      onCheckedChange={(c) => c && verifyCompliance(detail.id)}
                      data-testid="supplier-onboarding-compliance-checkbox"
                    />
                    Credit check & tax compliance verified
                    {detail.credit_tax_compliance_verified && (
                      <span className="text-[#0B7A56]">(by {detail.credit_tax_compliance_verified_by})</span>
                    )}
                  </label>
                  <Button
                    disabled={!detail.credit_tax_compliance_verified}
                    title={!detail.credit_tax_compliance_verified ? "Tick the checkbox above first" : undefined}
                    className="rounded-sm bg-[#0076CC] hover:bg-[#005FA3]" onClick={() => openFinancialModal(detail)} data-testid="supplier-onboarding-detail-activate-button"
                  >
                    <Wallet size={14} className="mr-1" /> {detail.activated_at ? "Update Financial Data" : "Update Financial Data & Activate"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feb 2026 REDESIGN: Update Financial Data (Payment Data / Bank Data / Tax Data) + Activate, all in one step */}
      <Dialog open={!!financialTarget} onOpenChange={(o) => { if (!o) { setFinancialTarget(null); setFinancialForm(null); } }}>
        <DialogContent className="rounded-sm max-w-xl max-h-[85vh] overflow-y-auto" data-testid="supplier-financial-data-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans">Update Financial Data in SAP{financialTarget ? ` — ${financialTarget.company_name}` : ""}</DialogTitle>
          </DialogHeader>
          {financialForm && (
            <div className="flex flex-col gap-5 text-sm">
              <div>
                <div className="text-xs font-bold uppercase text-[#5B738B] mb-2 flex items-center gap-1.5"><Buildings size={14} /> Payment Data</div>
                <div className="flex items-center gap-4 mb-2">
                  {PAYMENT_COMPANIES.map((c) => (
                    <label key={c.id} className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-testid={`financial-company-checkbox-${c.id}`}>
                      <Checkbox
                        checked={!!financialForm.companies[c.id]}
                        onCheckedChange={(v) => setFinancialForm((f) => ({ ...f, companies: { ...f.companies, [c.id]: !!v } }))}
                      />
                      Company {c.label}
                    </label>
                  ))}
                </div>
                <Label className="text-xs text-[#5B738B]">Account Determination Group</Label>
                <Select
                  value={financialForm.account_determination_group}
                  onValueChange={(v) => setFinancialForm((f) => ({ ...f, account_determination_group: v }))}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="financial-account-determination-group-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_DETERMINATION_GROUPS.map((g) => (
                      <SelectItem key={g} value={g}>{g}{ACCOUNT_DETERMINATION_GROUP_NAMES[g] ? ` — ${ACCOUNT_DETERMINATION_GROUP_NAMES[g]}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#98A2B3] mt-1">Leave both companies unchecked to skip Payment Data entirely.</p>

                <Label className="text-xs text-[#5B738B] mt-3 block">Payment Term</Label>
                <Select
                  value={financialForm.cash_discount_terms_code}
                  onValueChange={(v) => setFinancialForm((f) => ({ ...f, cash_discount_terms_code: v }))}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="financial-payment-term-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((t) => (
                      <SelectItem key={t} value={t}>{t}{PAYMENT_TERM_NAMES[t] ? ` — ${PAYMENT_TERM_NAMES[t]}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#98A2B3] mt-1">"0001" is the most common Payment Term across your existing suppliers.</p>
              </div>

              <div>
                <div className="text-xs font-bold uppercase text-[#5B738B] mb-2 flex items-center gap-1.5"><Wallet size={14} /> Bank Data</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-[#5B738B]">Bank Account Number</Label>
                    <Input className="rounded-sm mt-1" value={financialForm.bank_account_number} onChange={(e) => setFinancialForm((f) => ({ ...f, bank_account_number: e.target.value }))} data-testid="financial-bank-account-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5B738B]">IFSC</Label>
                    <Input className="rounded-sm mt-1 font-data" value={financialForm.bank_ifsc} onChange={(e) => setFinancialForm((f) => ({ ...f, bank_ifsc: e.target.value }))} data-testid="financial-bank-ifsc-input" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-[#5B738B]">Account Holder Name</Label>
                    <Input className="rounded-sm mt-1" value={financialForm.bank_account_holder_name} onChange={(e) => setFinancialForm((f) => ({ ...f, bank_account_holder_name: e.target.value }))} data-testid="financial-bank-holder-input" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-[#5B738B]">Bank (SAP Bank Directory)</Label>
                    {(() => {
                      const selectedBank = SAP_BANK_DIRECTORY.find((b) => b.nationalBankCode === financialForm.bank_routing_id);
                      return (
                        <>
                          <Popover open={bankPickerOpen} onOpenChange={setBankPickerOpen}>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="w-full mt-1 h-9 px-2.5 text-sm rounded-sm border border-[#D0D5DD] bg-white flex items-center justify-between gap-1 text-left text-[#1D2939] hover:border-[#98A2B3]"
                                data-testid="financial-bank-routing-id-picker"
                              >
                                <span className="truncate">
                                  {selectedBank ? `${selectedBank.bankName} (${selectedBank.nationalBankCode})` : "Search bank by name or code..."}
                                </span>
                                <CaretDown size={13} className="shrink-0 text-[#667085]" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-[26rem] p-0" data-testid="financial-bank-routing-id-popover">
                              <Command
                                shouldFilter={true}
                                filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
                              >
                                <CommandInput
                                  placeholder="Search bank name, city or code..."
                                  className="text-[13px]"
                                  data-testid="financial-bank-routing-id-search"
                                  autoFocus
                                />
                                <CommandList className="max-h-72">
                                  <CommandEmpty className="text-[13px] text-[#98A2B3] py-4 text-center">No bank found.</CommandEmpty>
                                  <CommandGroup>
                                    {SAP_BANK_DIRECTORY.map((b) => (
                                      <CommandItem
                                        key={b.bankId}
                                        value={`${b.bankName} ${b.city} ${b.country} ${b.nationalBankCode}`}
                                        onSelect={() => {
                                          setFinancialForm((f) => ({ ...f, bank_routing_id: b.nationalBankCode }));
                                          setBankPickerOpen(false);
                                        }}
                                        className="text-[13px] cursor-pointer"
                                        data-testid={`financial-bank-routing-id-option-${b.bankId}`}
                                      >
                                        <span className="truncate">{b.bankName}{b.city ? `, ${b.city}` : ""} <span className="text-[#98A2B3]">({b.country} · {b.nationalBankCode})</span></span>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          {selectedBank && (
                            <div className="mt-2 rounded-sm border border-[#E4E7EC] bg-[#F9FAFB] p-2.5 text-[11px] text-[#475467] grid grid-cols-2 gap-x-3 gap-y-1" data-testid="financial-bank-details-card">
                              <div className="col-span-2 flex items-center gap-1.5 font-bold text-[#1D2939] text-xs"><Bank size={14} /> {selectedBank.bankName}</div>
                              <div><span className="text-[#98A2B3]">City:</span> {selectedBank.city || "—"}</div>
                              <div><span className="text-[#98A2B3]">Country:</span> {selectedBank.country || "—"}</div>
                              <div><span className="text-[#98A2B3]">Routing Code:</span> <span className="font-data">{selectedBank.nationalBankCode}</span></div>
                              <div><span className="text-[#98A2B3]">Code Type:</span> {selectedBank.nationalBankCodeType || "—"}</div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                <p className="text-[11px] text-[#98A2B3] mt-1">
                  Picked from SAP's own Bank Directory — leave unselected to skip Bank Data for now.
                </p>
              </div>

              <div>
                <div className="text-xs font-bold uppercase text-[#5B738B] mb-2 flex items-center gap-1.5"><Shield size={14} /> Tax Data</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-[#5B738B]">GSTIN</Label>
                    <Input className="rounded-sm mt-1 font-data" value={financialForm.gstin} onChange={(e) => setFinancialForm((f) => ({ ...f, gstin: e.target.value }))} data-testid="financial-gstin-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5B738B]">PAN</Label>
                    <Input className="rounded-sm mt-1 font-data" value={financialForm.pan} onChange={(e) => setFinancialForm((f) => ({ ...f, pan: e.target.value }))} data-testid="financial-pan-input" />
                  </div>
                </div>
                <p className="text-[11px] text-[#98A2B3] mt-1">
                  GSTIN and PAN both push automatically to SAP's Tax Data when filled in.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-[#EAECF0]">
                <Button variant="outline" className="rounded-sm" onClick={() => { setFinancialTarget(null); setFinancialForm(null); }}>Cancel</Button>
                <Button
                  disabled={submittingFinancial}
                  className="rounded-sm bg-[#0076CC] hover:bg-[#005FA3]" onClick={submitFinancialData} data-testid="financial-submit-button"
                >
                  <Rocket size={14} className="mr-1" />
                  {submittingFinancial ? "Pushing to SAP..." : financialTarget?.activated_at ? "Push Financial Data to SAP" : "Push Financial Data & Activate in SAP"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="supplier-onboarding-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-sans">Reject New Supplier Request</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Reason (visible to the requester)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="rounded-sm border-[#CBD3DB]"
            data-testid="supplier-onboarding-reject-reason-input"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              onClick={reject}
              disabled={!rejectReason.trim()}
              className="rounded-sm bg-[#E02424] hover:bg-[#B91C1C] transition-colors duration-150"
              data-testid="supplier-onboarding-reject-confirm-button"
            >
              Confirm Reject
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
